import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { useEnvironment } from '../components/EnvironmentContext';
import { ENVIRONMENT_DEMO, ENVIRONMENT_PRODUCTION } from '../../constants';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { showNotification, TYPE_ERROR } from '../../store/notifications/actions';

type Props = {
    productId: string;
};

const PreviewReader: FC<Props> = ({ productId }) => {
    const [showReader, setShowReader] = useState(false);
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const CLOSE_PREVIEW_MESSAGE = 'close preview';
    const backgroundRef = useRef<HTMLDivElement>(null);

    function handleClickOutside(event: Event) {
        if (backgroundRef.current && backgroundRef.current.contains(event.target as Node)) {
            setShowReader(false);
        }
    }

    const iframeWindow = document?.getElementById('iframe');

    const receiveMessage = useCallback(
        (event: MessageEvent & { originalEvent?: MessageEvent }) => {
            const eventData = event.data || event.originalEvent?.data;

            if (eventData === CLOSE_PREVIEW_MESSAGE) {
                setShowReader(false);
            }

            if (showReader && eventData === 'preview error') {
                dispatch(showNotification(t('notifications.productPreview.error'), TYPE_ERROR));
                setShowReader(false);
            }
        },
        [dispatch, showReader, t]
    );

    useEffect(() => {
        if (showReader) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        document.addEventListener('mousedown', handleClickOutside);
        window.addEventListener('message', receiveMessage);
        return () => {
            window.removeEventListener('message', receiveMessage);
            document.removeEventListener('mousedown', handleClickOutside);
            iframeWindow?.remove();
        };
    }, [iframeWindow, receiveMessage, showReader]);

    const environment = useEnvironment();

    const host =
        environment === ENVIRONMENT_PRODUCTION || environment === ENVIRONMENT_DEMO
            ? 'webpreview.borrowbox.com'
            : 'webpreview.borrowbox.io';

    const src = `https://${host}/preview/${productId}`;
    let epubPreview: ReactNode | null = null;
    if (showReader) {
        epubPreview = (
            <Portal isOpened={true}>
                <div className="epubPreview">
                    <div>
                        <iframe
                            frameBorder="0"
                            scrolling="no"
                            src={src}
                            name="epubPreview"
                            width="100%"
                            height="100%"
                            allowFullScreen={true}
                        />
                    </div>
                    <div className="background" ref={backgroundRef} />
                </div>
            </Portal>
        );
    }

    return (
        <div>
            {epubPreview}
            <div className="icn-preview">
                <i onClick={() => setShowReader(true)} className="ebooks" />
            </div>
        </div>
    );
};

export default PreviewReader;
